import { createSelector } from 'reselect';

const selectAllProjects = state => state.projects;
const allProjectData = state => state.projects.projects;
const projectFilterData = state => state.filters.projects;
const projectSort = state => state.sort.projects;

export const selectIsFetchingProjects = createSelector(
    [selectAllProjects],
    projects => projects.isFetchingProjects
)

export const selectProjects = createSelector(
    [selectAllProjects],
    projects => projects.projects
)

export const selectProjectsUsers = createSelector(
    [selectAllProjects],
    projects => projects.projectsUsers
)

export const selectProjectsGroups = createSelector(
    [selectAllProjects],
    projects => projects.projectsGroups
)

export const selectProjectCounts = createSelector( //Remove after completing MVC deprecation
    [selectAllProjects],
    projects => projects.projectCounts
)

export const selectProjectsCount = createSelector(
    [selectAllProjects],
    projects => projects.projectsCount
)

export const selectProjectQuery = createSelector(
    [selectAllProjects],
    projects => projects.projectQueryRedux
)

export const selectSelectedProjects = createSelector(
    [selectAllProjects],
    projects => projects.selectedProjects
)

// Normalize the legacy "Category" value (still present in persisted state for
// existing users) to the new "Status" value. Can be removed once redux-persist
// state has been migrated app-wide. See #TAD-5695.
export const selectGroupProjectsBy = createSelector(
    [selectAllProjects],
    projects => projects.groupProjectsBy === 'Category' ? 'Status' : projects.groupProjectsBy
)

export const selectProjectsGrouped = createSelector(
    [selectAllProjects],
    projects => projects.projectsGrouped
)

export const selectFilteredAndSortedProject = createSelector(
    selectAllProjects,
    projects => projects.filteredAndSortedProjects
)

export const selectFilteredData = createSelector(
    [selectProjectsGrouped, projectFilterData],
    (projectsGrouped, filters) => {

        let newProjectsGrouped = {};

        const appliedFilters = filters.appliedFilters;
        const conjunctionSelection = filters.conjunctionSelection.name;

        if(appliedFilters.length <= 0){
            return projectsGrouped;
        }
        // Note: a `fieldMappings` table used to remap Priority→strPriority,
        // Start→startDate, End→endDate. Those were legacy MVC field names that
        // no longer exist on the current API payload (the new API uses PascalCase
        // `Priority`, `Start`, `End`, matching the projectDataField in the filter
        // definitions). The mapping silently dropped these filters — applying a
        // Priority/Start/End filter would empty the project list. The case-
        // insensitive fallback below already handles the old MVC lowercase shape
        // (`priority`, `start`, `end`) for any persisted state still in flight.
        for( const group of Object.keys(projectsGrouped)) {
            const filteredData = projectsGrouped[group].filter((projectData) => {

                let filtersMatched = 0;
                let isInFilteredSet = false;

                appliedFilters.forEach((filterApplied) => {
                    const inclusionSelection = filterApplied.inclusionSelection.name;
                    const filterSelections = filterApplied.filterSelection;
                    let dataField = filterApplied.projectDataField;
                    if (!projectData.hasOwnProperty(dataField)) {
                        const lowerCaseField = dataField.toLowerCase();
                        if (projectData.hasOwnProperty(lowerCaseField)) {
                            dataField = lowerCaseField;
                        } else {
                            return;
                        }
                    }

                    let isInAppliedFilter = doesProjectDataMatchFilter(filterSelections, projectData, dataField);

                    if(filterSelections.length <= 0){
                        isInAppliedFilter = true;
                    }

                    if(inclusionSelection === 'Excludes'){
                        isInAppliedFilter = !isInAppliedFilter;
                    }

                    if(isInAppliedFilter){
                        filtersMatched++;
                    }
                })

                if(conjunctionSelection === 'And' && filtersMatched === appliedFilters.length){
                    isInFilteredSet = true;
                } else if (conjunctionSelection === 'Or' && filtersMatched > 0) {
                    isInFilteredSet = true;
                }

                return isInFilteredSet;
            })
             newProjectsGrouped[group] = filteredData;
        }

        return newProjectsGrouped;
    }

)

const doesProjectDataMatchFilter = (filterSelections, projectData, dataField) => {

    let projectDataFieldValues = [];

    if (dataField === "Members" || dataField === "members") {
        if (projectData.Groups && projectData.Groups.length > 0) {
            const updatedGroups = projectData.Groups.map(group => ({
                ...group,
                Id: group.GroupId + 99999
            }));
            projectDataFieldValues = [...updatedGroups, ...(projectData.Members || [])];
        } else {
            projectDataFieldValues = projectData.Members || [];
        }
    } else {
        projectDataFieldValues = projectData[dataField] || [];
    }

    if (projectDataFieldValues === null) {
        return false;
    }

    const projectDataFieldValueArray = formatProjectDataFieldValue(dataField, projectDataFieldValues);

    for (const filterSelection of filterSelections) {
        const individualFilterSelection = filterSelection.name;
        for(const projectData of projectDataFieldValueArray) {
            if (dataField === "Status" || dataField === "status") {
                if ((projectData == 1 && individualFilterSelection == "Active") ||
                    (projectData == 2 && individualFilterSelection == "Completed") ||
                    (projectData == 0 && individualFilterSelection == "Archived")) {
                    return true;
                }
            } else if (projectData === individualFilterSelection) {
                return true;
            }
        }
    }
    return false;
}

//Handles all types of data and returns a string array of just that data
// Eg [{name: A}, {name:B}] => [A,B]
const formatProjectDataFieldValue = (dataField, projectDataFieldValues) => {
    switch (dataField) {
        case 'Tags':
        case 'tags': // Remove after MVC deprecation is completed
            return projectDataFieldValues;
        case 'Members':
            return projectDataFieldValues.map((projectDataFieldValue) => projectDataFieldValue.User.Name);
        case 'members': // Remove after MVC deprecation is completed
            return projectDataFieldValues.map((projectDataFieldValue) => projectDataFieldValue.userNameFull);
        case 'Start':
        case 'startDate': // Remove after MVC deprecation is completed
        case 'End':
        case 'endDate': // Remove after MVC deprecation is completed
            let formattedDateData = projectDataFieldValues.split('T')[0];
            formattedDateData = [formattedDateData];
            return formattedDateData;
        case 'Name':
            return [projectDataFieldValues];
        default:
            return [projectDataFieldValues]
    }
}

export const selectFilteredProjectsSorted = (labels) => createSelector(
    [selectFilteredData, projectSort],
    (filteredProjects, {sortSelection, sortOrder}) => {

        if(!sortSelection){
            return filteredProjects;
        }

        let newProjectsGrouped = {};

        for( const group of Object.keys(filteredProjects)) {
            const newSortedProjects = [...filteredProjects[group]];

            newSortedProjects.sort((firstElem, secondElem) => {
                let comparisonResult = comparisonFunction(sortSelection, firstElem, secondElem, labels);
                if(sortOrder.name === 'Descending') comparisonResult *= -1;
                return comparisonResult;
            });

            newProjectsGrouped[group] = newSortedProjects;
        }
        return newProjectsGrouped;
    }
)

const comparisonFunction = ({projectField, name}, firstElem, secondElem, labels) => {
    let firstElemValue = firstElem[projectField] || '';
    let secondElemValue = secondElem[projectField] || '';

    switch (name) {
        case 'Project name':
        case `${labels?.ProjectSingular ?? "Project"} names`:
        case 'Start date':
        case 'Due date':
            return firstElemValue.localeCompare(secondElemValue);
        case 'Priority':
            const priorityOrder = ['Low','Medium','High','Urgent'];
            return priorityOrder.indexOf(firstElemValue) - priorityOrder.indexOf(secondElemValue);
        case 'Number of tasks':
        case `Number of ${labels?.TaskPlural ?? "tasks"}`:
            return firstElemValue - secondElemValue;
        default:
            return 0;
    }
}
